import React, { useState, useMemo, useEffect } from 'react';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import {
  OwnerEntity, getCurrentUser, getAccountOwner, getAccountOwnerForUser, resolveEntities,
} from '../utils/ownerEntities';
import { entityBaseCurrency } from '../utils/baseCurrency';
import { getProfitCenters } from '../utils/profitCenters';
import './GLReportsTab.css';
import './FinancialStatementsTab.css';

// =============================================
// Financial Statements (Reports → read-only): Income Statement + Balance Sheet
// =============================================
// Built on the same GL data as the Trial Balance. Each account is classified by its
// Financial-Statement header (ASSET/LIAB/EQ/INC/EXP via finsta_cate_id → cate →
// header). Income Statement = Income − Expense over a PERIOD; Balance Sheet = Asset
// vs Liability + Equity AS OF a date, with Net Income rolled into Equity. Computed
// from POSTED (non-void) journals + opening balances. Bookkeeping only, never DMS.
// =============================================

interface FinancialStatementsTabProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

interface CoaRow {
  coa_id: number; account_owner_id: number; owner_type: string; owner_id: number;
  account_number: string | null; account_name: string; normal_balance?: string;
  opening_balance?: number | string | null; finsta_cate_id?: number | null;
  is_header: number; is_active?: number; is_deleted?: number; [k: string]: any;
}
interface JournalRow { journal_id: number; account_owner_id: number; owner_type: string; owner_id: number; journal_date: string; status: string; is_deleted?: number; [k: string]: any; }
interface JournalLineRow { line_id: number; journal_id: number; coa_id: number; debit: number; credit: number; is_deleted?: number; [k: string]: any; }

type HeaderCode = 'ASSET' | 'LIAB' | 'EQ' | 'INC' | 'EXP';
const num = (v: any) => Number(v) || 0;
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FinancialStatementsTab: React.FC<FinancialStatementsTabProps> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<CoaRow[]>([]);
  const [journals, setJournals] = useState<JournalRow[]>([]);
  const [journalLines, setJournalLines] = useState<JournalLineRow[]>([]);
  const [cates, setCates] = useState<Record<string, any>[]>([]);
  const [headers, setHeaders] = useState<Record<string, any>[]>([]);
  const [locationsData, setLocationsData] = useState<Record<string, any>[]>([]);
  const [unitsData, setUnitsData] = useState<Record<string, any>[]>([]);

  const [ownerKey, setOwnerKey] = useState('');
  const [view, setView] = useState<'pnl' | 'bs' | 'bypc'>('pnl');
  const [fromDate, setFromDate] = useState('');
  const [asOfDate, setAsOfDate] = useState('');
  const [profitCenter, setProfitCenter] = useState(''); // '' = all (Income Statement only)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, b, coa, j, jl, ct, hd, loc, un] = await Promise.all([
          loadTable('users'), loadTable('user_businesses'), loadTable('acctg_coa'),
          loadTable('acctg_gl_journal'), loadTable('acctg_gl_journal_line'),
          loadTable('acctg_finsta_cate'), loadTable('acctg_finsta_headers'),
          loadTable('user_business_location'), loadTable('user_business_unit'),
        ]);
        if (cancelled) return;
        setUsersData(u); setBusinessesData(b); setCoaAccounts(coa as CoaRow[]);
        setJournals(j as JournalRow[]); setJournalLines(jl as JournalLineRow[]);
        setCates(ct); setHeaders(hd);
        setLocationsData(loc); setUnitsData(un); setDataLoaded(true);
      } catch {
        if (cancelled) return;
        setUsersData(getTableData('users')); setBusinessesData(getTableData('user_businesses'));
        setCoaAccounts(getTableData('acctg_coa') as CoaRow[]);
        setJournals(getTableData('acctg_gl_journal') as JournalRow[]);
        setJournalLines(getTableData('acctg_gl_journal_line') as JournalLineRow[]);
        setCates(getTableData('acctg_finsta_cate')); setHeaders(getTableData('acctg_finsta_headers'));
        setLocationsData(getTableData('user_business_location'));
        setUnitsData(getTableData('user_business_unit')); setDataLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwnerId = useMemo(() => {
    if (!currentUser) return 0;
    const ao = getAccountOwnerForUser(currentUser.user_id, usersData); // logged-in user's own account (multi-tenant safe)
    return ao ? Number(ao.user_id) : 0;
  }, [currentUser, usersData]);

  const ownerEntities = useMemo<OwnerEntity[]>(
    () => dataLoaded ? resolveEntities(viewMode, accountOwnerId, selectedClientId, usersData, businessesData) : [],
    [dataLoaded, viewMode, accountOwnerId, selectedClientId, usersData, businessesData]);
  useEffect(() => {
    if (!ownerKey && ownerEntities.length) setOwnerKey(`${ownerEntities[0].owner_type}:${ownerEntities[0].owner_id}`);
  }, [ownerEntities, ownerKey]);
  const selectedEntity = useMemo(() => ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === ownerKey) || null, [ownerEntities, ownerKey]);
  // The books' base currency (Business setup / the entity's country) — named on the report.
  const reportCurrency = useMemo(
    () => (selectedEntity ? entityBaseCurrency(selectedEntity.owner_type, selectedEntity.owner_id, usersData, businessesData) : ''),
    [selectedEntity, usersData, businessesData]);

  // Profit centers (business locations/units flagged tracks_own_profitability). The
  // filter applies to the Income Statement only (a P&L concept), not the Balance Sheet.
  const profitCenters = useMemo(
    () => getProfitCenters(selectedEntity && selectedEntity.owner_type === 'business' ? selectedEntity.owner_id : null, locationsData, unitsData),
    [selectedEntity, locationsData, unitsData]);
  const pcFilter = useMemo(() => profitCenters.decode(profitCenter), [profitCenters, profitCenter]);

  // coa_id → header code (ASSET/LIAB/EQ/INC/EXP), via finsta_cate_id → cate.header_id → header.header_code.
  const headerByCoa = useMemo(() => {
    const headerCodeById = new Map<number, string>();
    headers.forEach(h => headerCodeById.set(Number(h.header_id), String(h.header_code || h.header_name || '').toUpperCase()));
    const cateToHeader = new Map<number, number>();
    cates.forEach(c => cateToHeader.set(Number(c.cate_id), Number(c.header_id)));
    const m = new Map<number, HeaderCode | null>();
    coaAccounts.forEach(a => {
      const hid = a.finsta_cate_id != null ? cateToHeader.get(Number(a.finsta_cate_id)) : undefined;
      const code = hid != null ? headerCodeById.get(hid) : undefined;
      const norm = (code || '').replace('LIABILITY', 'LIAB').replace('EQUITY', 'EQ').replace('INCOME', 'INC').replace('EXPENSE', 'EXP');
      m.set(Number(a.coa_id), (['ASSET', 'LIAB', 'EQ', 'INC', 'EXP'].includes(norm) ? norm : null) as HeaderCode | null);
    });
    return m;
  }, [coaAccounts, cates, headers]);

  const entityAccounts = useMemo(() => coaAccounts.filter(a =>
    a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
    // eslint-disable-next-line eqeqeq
    a.account_owner_id == accountOwnerId && selectedEntity &&
    a.owner_type === selectedEntity.owner_type &&
    // eslint-disable-next-line eqeqeq
    a.owner_id == selectedEntity.owner_id
  ), [coaAccounts, accountOwnerId, selectedEntity]);

  // Posted, non-void journal ids for the entity, with date — filtered by an upper bound (and lower for P&L).
  const linesUpTo = useMemo(() => {
    if (!selectedEntity) return [] as JournalLineRow[];
    const okJournal = new Map<number, string>();
    journals.forEach(j => {
      if (j.is_deleted === 1 || j.status !== 'posted') return;
      // eslint-disable-next-line eqeqeq
      if (j.owner_type !== selectedEntity.owner_type || j.owner_id != selectedEntity.owner_id || j.account_owner_id != accountOwnerId) return;
      okJournal.set(Number(j.journal_id), (j.journal_date || '').slice(0, 10));
    });
    return journalLines
      .filter(l => l.is_deleted !== 1 && okJournal.has(Number(l.journal_id)))
      .map(l => ({ ...l, _date: okJournal.get(Number(l.journal_id))! }));
  }, [journals, journalLines, selectedEntity, accountOwnerId]);

  // Sum (debit, credit) for an account over lines passing a date predicate. When
  // applyPc is true (Income Statement), also restrict to the selected profit center:
  // a unit filter matches unit_id; a location filter matches location_id (its units too).
  const sumFor = useMemo(() => (coaId: number, pred: (d: string) => boolean, applyPc = false) => {
    let debit = 0, credit = 0;
    linesUpTo.forEach((l: any) => {
      if (Number(l.coa_id) !== coaId || !pred(l._date)) return;
      if (applyPc) {
        if (pcFilter.unit_id != null) { if (Number(l.unit_id) !== Number(pcFilter.unit_id)) return; }
        // eslint-disable-next-line eqeqeq
        else if (pcFilter.location_id != null) { if (!(l.location_id != null && l.location_id == pcFilter.location_id)) return; }
      }
      debit += num(l.debit); credit += num(l.credit);
    });
    return { debit: round2(debit), credit: round2(credit) };
  }, [linesUpTo, pcFilter]);

  // ---- Income Statement (period fromDate..asOfDate) ----
  const incomeStatement = useMemo(() => {
    const inRange = (d: string) => (!fromDate || d >= fromDate) && (!asOfDate || d <= asOfDate);
    const revenue: { acct: CoaRow; amount: number }[] = [];
    const expenses: { acct: CoaRow; amount: number }[] = [];
    entityAccounts.forEach(a => {
      const code = headerByCoa.get(Number(a.coa_id));
      if (code !== 'INC' && code !== 'EXP') return;
      const { debit, credit } = sumFor(Number(a.coa_id), inRange, true);
      if (code === 'INC') { const amt = round2(credit - debit); if (amt !== 0) revenue.push({ acct: a, amount: amt }); }
      else { const amt = round2(debit - credit); if (amt !== 0) expenses.push({ acct: a, amount: amt }); }
    });
    const totalRevenue = round2(revenue.reduce((s, r) => s + r.amount, 0));
    const totalExpenses = round2(expenses.reduce((s, r) => s + r.amount, 0));
    return { revenue, expenses, totalRevenue, totalExpenses, netIncome: round2(totalRevenue - totalExpenses) };
  }, [entityAccounts, headerByCoa, sumFor, fromDate, asOfDate]);

  // ---- Income Statement by Profit Center (matrix; period fromDate..asOfDate) ----
  // Columns = each profit center (flagged locations/units) + an "Unassigned" bucket
  // for P&L lines with no profit center. Each line falls in exactly one column
  // (a line tagged to a unit → that unit's column; tagged to a location → that
  // location's column; untagged → Unassigned), so the columns sum to the Total.
  const pnlByPc = useMemo(() => {
    const inRange = (d: string) => (!fromDate || d >= fromDate) && (!asOfDate || d <= asOfDate);
    const cols = profitCenters.options.map(o => {
      const { location_id, unit_id } = profitCenters.decode(o.value);
      return { key: o.value, label: o.label, location_id, unit_id, depth: o.depth || 0 };
    });
    const ncol = cols.length + 1;              // + Unassigned (last)
    const UNASSIGNED = ncol - 1;
    const colIndexForLine = (l: any): number => {
      if (l.unit_id != null) {
        const i = cols.findIndex(c => c.unit_id != null && Number(c.unit_id) === Number(l.unit_id));
        if (i >= 0) return i;
      }
      if (l.location_id != null && l.unit_id == null) {
        const i = cols.findIndex(c => c.unit_id == null && c.location_id != null && Number(c.location_id) === Number(l.location_id));
        if (i >= 0) return i;
      }
      return UNASSIGNED;
    };
    const revByAcct = new Map<number, number[]>();
    const expByAcct = new Map<number, number[]>();
    const ensure = (m: Map<number, number[]>, id: number) => {
      let a = m.get(id); if (!a) { a = new Array(ncol).fill(0); m.set(id, a); } return a;
    };
    linesUpTo.forEach((l: any) => {
      if (!inRange(l._date)) return;
      const code = headerByCoa.get(Number(l.coa_id));
      if (code !== 'INC' && code !== 'EXP') return;
      const ci = colIndexForLine(l);
      const amt = code === 'INC' ? (num(l.credit) - num(l.debit)) : (num(l.debit) - num(l.credit));
      ensure(code === 'INC' ? revByAcct : expByAcct, Number(l.coa_id))[ci] += amt;
    });
    const acctById = new Map(entityAccounts.map(a => [Number(a.coa_id), a]));
    const buildRows = (m: Map<number, number[]>) => Array.from(m.entries())
      .map(([coaId, arr]) => ({ acct: acctById.get(coaId)!, amounts: arr.map(round2), total: round2(arr.reduce((s, x) => s + x, 0)) }))
      .filter(r => r.acct && r.total !== 0)
      .sort((a, b) => (a.acct.account_number || '').localeCompare(b.acct.account_number || ''));
    const revenue = buildRows(revByAcct);
    const expenses = buildRows(expByAcct);
    const sumCol = (rows: { amounts: number[] }[], ci: number) => round2(rows.reduce((s, r) => s + r.amounts[ci], 0));
    const idxs = Array.from({ length: ncol }, (_, i) => i);
    const totalRevenue = idxs.map(ci => sumCol(revenue, ci));
    const totalExpenses = idxs.map(ci => sumCol(expenses, ci));
    const netIncome = idxs.map(ci => round2(totalRevenue[ci] - totalExpenses[ci]));
    const showUnassigned = totalRevenue[UNASSIGNED] !== 0 || totalExpenses[UNASSIGNED] !== 0;
    // Visible columns: every profit center, then Unassigned only if it has activity.
    const visibleCols = cols.map((c, i) => ({ ...c, idx: i }));
    if (showUnassigned) visibleCols.push({ key: '__unassigned__', label: 'Unassigned', location_id: null, unit_id: null, depth: 0, idx: UNASSIGNED });
    return { visibleCols, revenue, expenses, totalRevenue, totalExpenses, netIncome };
  }, [profitCenters, linesUpTo, headerByCoa, entityAccounts, fromDate, asOfDate]);

  // ---- Balance Sheet (as of asOfDate; cumulative) ----
  const balanceSheet = useMemo(() => {
    const upTo = (d: string) => (!asOfDate || d <= asOfDate);
    const assets: { acct: CoaRow; amount: number }[] = [];
    const liabilities: { acct: CoaRow; amount: number }[] = [];
    const equity: { acct: CoaRow; amount: number }[] = [];
    let netIncome = 0;
    entityAccounts.forEach(a => {
      const code = headerByCoa.get(Number(a.coa_id));
      const { debit, credit } = sumFor(Number(a.coa_id), upTo);
      const opening = round2(num(a.opening_balance));
      if (code === 'ASSET') { const amt = round2(opening + (debit - credit)); if (amt !== 0) assets.push({ acct: a, amount: amt }); }
      else if (code === 'LIAB') { const amt = round2(opening + (credit - debit)); if (amt !== 0) liabilities.push({ acct: a, amount: amt }); }
      else if (code === 'EQ') { const amt = round2(opening + (credit - debit)); if (amt !== 0) equity.push({ acct: a, amount: amt }); }
      else if (code === 'INC') { netIncome = round2(netIncome + (credit - debit)); }
      else if (code === 'EXP') { netIncome = round2(netIncome - (debit - credit)); }
    });
    const totalAssets = round2(assets.reduce((s, r) => s + r.amount, 0));
    const totalEquityAccts = round2(equity.reduce((s, r) => s + r.amount, 0));
    const totalLiabilities = round2(liabilities.reduce((s, r) => s + r.amount, 0));
    const totalEquity = round2(totalEquityAccts + netIncome);
    return { assets, liabilities, equity, netIncome, totalAssets, totalLiabilities, totalEquity, diff: round2(totalAssets - (totalLiabilities + totalEquity)) };
  }, [entityAccounts, headerByCoa, sumFor, asOfDate]);

  // Detail accounts with no recognized FS header — excluded from the statements (flag it).
  const unclassified = useMemo(() => entityAccounts.filter(a => !headerByCoa.get(Number(a.coa_id))), [entityAccounts, headerByCoa]);

  if (!dataLoaded) return <div className="glr-tab"><div className="glr-empty">Loading…</div></div>;
  const noEntity = ownerEntities.length === 0;
  const acctLabel = (a: CoaRow) => `${a.account_number ? a.account_number + ' · ' : ''}${a.account_name}`;

  const section = (title: string, rows: { acct: CoaRow; amount: number }[], total: number, extra?: { label: string; amount: number }) => (
    <table className="glr-table fs-section">
      <thead><tr><th>{title}</th><th className="glr-num">Amount</th></tr></thead>
      <tbody>
        {rows.length === 0 && !extra ? (
          <tr><td className="fs-none" colSpan={2}>None</td></tr>
        ) : rows.map(r => (
          <tr key={r.acct.coa_id}><td>{acctLabel(r.acct)}</td><td className="glr-num">{money(r.amount)}</td></tr>
        ))}
        {extra && <tr className="fs-extra"><td>{extra.label}</td><td className="glr-num">{money(extra.amount)}</td></tr>}
      </tbody>
      <tfoot><tr className="glr-total-row"><td>Total {title}</td><td className="glr-num">{money(total)}</td></tr></tfoot>
    </table>
  );

  return (
    <div className="glr-tab">
      <div className="glr-head">
        <h3 className="glr-title">Financial Statements</h3>
        <p className="glr-sub">Income Statement and Balance Sheet from posted journal entries. Accounts are grouped by their Financial-Statement category.{reportCurrency ? ` Amounts in ${reportCurrency}.` : ''}</p>
      </div>

      {noEntity ? (
        <div className="glr-empty">
          {viewMode === 'clients' && selectedClientId == null ? 'Pick a client first.' : 'No accounts available — set up your Chart of Accounts first.'}
        </div>
      ) : (
        <>
          <div className="glr-controls">
            <div className="glr-field">
              <label className="glr-label">Entity</label>
              <SearchableDropdown
                className="glr-owner-select"
                options={ownerEntities.map(e => ({ value: `${e.owner_type}:${e.owner_id}`, label: `${e.display_name} (${e.badge})` }))}
                value={ownerKey}
                onChange={(v: string) => { setOwnerKey(v); setProfitCenter(''); }}
                placeholder="Choose an entity"
              />
            </div>
            {view === 'pnl' && profitCenters.hasAny && (
              <div className="glr-field">
                <label className="glr-label">Profit center</label>
                <SearchableDropdown
                  className="glr-owner-select"
                  options={[{ value: '', label: 'All profit centers' }, ...profitCenters.options]}
                  value={profitCenter}
                  onChange={(v: string) => setProfitCenter(v)}
                  placeholder="All profit centers"
                />
              </div>
            )}
            {(view === 'pnl' || view === 'bypc') && (
              <div className="glr-field">
                <label className="glr-label">From</label>
                <input type="date" className="glr-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
              </div>
            )}
            <div className="glr-field">
              <label className="glr-label">{view === 'bs' ? 'As of' : 'To'}</label>
              <input type="date" className="glr-input" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} />
            </div>
          </div>

          <div className="glr-viewtabs">
            <button className={`glr-viewtab ${view === 'pnl' ? 'active' : ''}`} onClick={() => setView('pnl')}>Income Statement</button>
            <button className={`glr-viewtab ${view === 'bs' ? 'active' : ''}`} onClick={() => setView('bs')}>Balance Sheet</button>
            {profitCenters.hasAny && (
              <button className={`glr-viewtab ${view === 'bypc' ? 'active' : ''}`} onClick={() => setView('bypc')}>P&amp;L by Profit Center</button>
            )}
          </div>

          {view === 'pnl' ? (
            <div className="fs-body">
              {section('Revenue', incomeStatement.revenue, incomeStatement.totalRevenue)}
              {section('Expenses', incomeStatement.expenses, incomeStatement.totalExpenses)}
              <div className={`fs-net ${incomeStatement.netIncome >= 0 ? 'pos' : 'neg'}`}>
                <span>Net {incomeStatement.netIncome >= 0 ? 'Income' : 'Loss'}</span>
                <span className="glr-num">{money(incomeStatement.netIncome)}</span>
              </div>
            </div>
          ) : view === 'bypc' ? (
            <div className="fs-body">
              {(() => {
                const { visibleCols, revenue, expenses, totalRevenue, totalExpenses, netIncome } = pnlByPc;
                const cell = (n: number) => n !== 0 ? money(n) : '';
                if (revenue.length === 0 && expenses.length === 0) {
                  return <div className="glr-empty glr-empty-sm">No income or expense activity{(fromDate || asOfDate) ? ' in this date range' : ''}.</div>;
                }
                return (
                  <div className="fs-pc-scroll">
                    <table className="glr-table fs-pc-matrix">
                      <thead>
                        <tr>
                          <th className="fs-pc-acct">Account</th>
                          {visibleCols.map(c => (
                            <th key={c.key} className="glr-num" style={c.depth ? { paddingLeft: 18 } : undefined}>{c.label}</th>
                          ))}
                          <th className="glr-num fs-pc-total">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="fs-pc-group"><td colSpan={visibleCols.length + 2}>Revenue</td></tr>
                        {revenue.length === 0 ? (
                          <tr><td className="fs-none" colSpan={visibleCols.length + 2}>None</td></tr>
                        ) : revenue.map(r => (
                          <tr key={`inc-${r.acct.coa_id}`}>
                            <td>{r.acct.account_number ? `${r.acct.account_number} · ` : ''}{r.acct.account_name}</td>
                            {visibleCols.map(c => <td key={c.key} className="glr-num">{cell(r.amounts[c.idx])}</td>)}
                            <td className="glr-num fs-pc-total">{cell(r.total)}</td>
                          </tr>
                        ))}
                        <tr className="fs-pc-subtotal">
                          <td>Total Revenue</td>
                          {visibleCols.map(c => <td key={c.key} className="glr-num">{cell(totalRevenue[c.idx])}</td>)}
                          <td className="glr-num fs-pc-total">{cell(revenue.reduce((s, r) => s + r.total, 0))}</td>
                        </tr>
                        <tr className="fs-pc-group"><td colSpan={visibleCols.length + 2}>Expenses</td></tr>
                        {expenses.length === 0 ? (
                          <tr><td className="fs-none" colSpan={visibleCols.length + 2}>None</td></tr>
                        ) : expenses.map(r => (
                          <tr key={`exp-${r.acct.coa_id}`}>
                            <td>{r.acct.account_number ? `${r.acct.account_number} · ` : ''}{r.acct.account_name}</td>
                            {visibleCols.map(c => <td key={c.key} className="glr-num">{cell(r.amounts[c.idx])}</td>)}
                            <td className="glr-num fs-pc-total">{cell(r.total)}</td>
                          </tr>
                        ))}
                        <tr className="fs-pc-subtotal">
                          <td>Total Expenses</td>
                          {visibleCols.map(c => <td key={c.key} className="glr-num">{cell(totalExpenses[c.idx])}</td>)}
                          <td className="glr-num fs-pc-total">{cell(expenses.reduce((s, r) => s + r.total, 0))}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr className="fs-pc-net">
                          <td>Net Income</td>
                          {visibleCols.map(c => <td key={c.key} className="glr-num">{cell(netIncome[c.idx])}</td>)}
                          <td className="glr-num fs-pc-total">{cell(revenue.reduce((s, r) => s + r.total, 0) - expenses.reduce((s, r) => s + r.total, 0))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="fs-body">
              {section('Assets', balanceSheet.assets, balanceSheet.totalAssets)}
              {section('Liabilities', balanceSheet.liabilities, balanceSheet.totalLiabilities)}
              {section('Equity', balanceSheet.equity, balanceSheet.totalEquity, { label: 'Net Income (current earnings)', amount: balanceSheet.netIncome })}
              <div className="fs-net">
                <span>Liabilities + Equity</span>
                <span className="glr-num">{money(round2(balanceSheet.totalLiabilities + balanceSheet.totalEquity))}</span>
              </div>
              <div className={`glr-balance-row ${balanceSheet.diff === 0 ? 'ok' : 'off'} fs-check`}>
                {balanceSheet.diff === 0
                  ? 'Balanced — assets equal liabilities plus equity.'
                  : `Out of balance by ${money(Math.abs(balanceSheet.diff))} — opening balances may not net to zero, or some accounts are unclassified.`}
              </div>
            </div>
          )}

          {unclassified.length > 0 && (
            <div className="fs-unclassified">
              {unclassified.length} account{unclassified.length === 1 ? '' : 's'} have no Financial-Statement category and are excluded: {unclassified.map(a => a.account_name).join(', ')}. Set a category in Chart of Accounts to include them.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FinancialStatementsTab;
